import React, { useState, useEffect } from 'react';
import { BarChart3, ShieldAlert } from 'lucide-react';
import { fetchMenuOptionUsage, fetchMenuErrorRates } from '../services/api';

export default function MenuUsageAndErrors({ mno, route, forceMock }) {
  const [options, setOptions] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    Promise.all([
      fetchMenuOptionUsage({ mno, route, forceMock }),
      fetchMenuErrorRates({ mno, route, forceMock }),
    ]).then(([opts, errs]) => {
      if (isMounted) {
        setOptions(opts || []);
        setErrors(errs || []);
        setLoading(false);
      }
    }).catch(() => {
      if (isMounted) setLoading(false);
    });

    return () => { isMounted = false; };
  }, [mno, route, forceMock]);

  return (
    <div className="two-col-grid">
      
      {/* 1. Menu Option Popularity */}
      <div className="glass-card" style={{ padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <BarChart3 size={18} color="var(--accent-orange)" />
          <h2 style={{ fontSize: '16px', fontWeight: '700' }}>Menu Options Popularity</h2>
        </div>
        <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
          Distribution of user selections from the Root/Main Banking Menu
        </p>

        {loading ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Loading menu options...</div>
        ) : options.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '13px', padding: '20px 0', textAlign: 'center' }}>
            No option data recorded yet for the selected filter.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {options.map((opt, i) => {
              const label = opt.optionSelected || opt.selectedOption || `Menu Option ${i + 1}`;
              const count = opt.selectionCount ?? opt.count ?? 0;
              const pct = opt.percentage ?? 0;
              const color = i === 0 ? '#F84122' : i === 1 ? '#0F172A' : i === 2 ? '#2563EB' : '#10B981';

              return (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '6px',
                        background: `${color}18`,
                        color: color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: '700',
                        fontFamily: 'var(--font-mono)'
                      }}>
                        {i + 1}
                      </span>
                      <span style={{ fontWeight: '700', color: 'var(--text-primary)' }}>
                        {label}
                      </span>
                    </div>
                    <div>
                      <strong style={{ color: color, fontSize: '14px', fontWeight: '800' }}>{pct}%</strong>
                      <span style={{ color: 'var(--text-muted)', fontSize: '11px', marginLeft: '6px' }}>
                        ({count.toLocaleString()} sessions)
                      </span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div style={{ width: '100%', height: '8px', background: '#F1F5F9', borderRadius: '999px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.max(pct, 5)}%`,
                      height: '100%',
                      background: color,
                      borderRadius: '999px',
                      transition: 'width 0.6s ease'
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 2. Error Hotspots Table */}
      <div className="glass-card" style={{ padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <ShieldAlert size={18} color="var(--accent-rose)" />
          <h2 style={{ fontSize: '16px', fontWeight: '700' }}>Error & Friction Hotspots</h2>
        </div>
        <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '18px' }}>
          Screens with highest invalid input and timeout frequencies
        </p>

        {loading ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Loading error metrics...</div>
        ) : errors.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No error hotspots detected.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {errors.map((err, i) => (
              <div
                key={i}
                style={{
                  padding: '12px 14px',
                  background: '#F8FAFC',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                    {err.menuState}
                  </span>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    color: 'var(--accent-rose)',
                    background: 'rgba(239, 68, 68, 0.1)',
                    padding: '2px 8px',
                    borderRadius: '999px'
                  }}>
                    {err.errorRate}% error rate
                  </span>
                </div>

                {/* Top reasons */}
                {err.topErrorReasons && err.topErrorReasons.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {err.topErrorReasons.map((reason, idx) => (
                      <span
                        key={idx}
                        style={{
                          fontSize: '11px',
                          color: 'var(--text-secondary)',
                          background: '#E2E8F0',
                          padding: '2px 8px',
                          borderRadius: '4px'
                        }}
                      >
                        {reason}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
